import React, { useEffect, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { usePage } from "@inertiajs/react";
import { Link } from "@inertiajs/react";

import ReadMoreText from "@/Components/ReadMoreText/ReadMoreText";
import UserOne from "../../images/user/user-00.png";
import ApiService from "@/Services/ApiService";
import SkeletonCard from "@/Components/Skeletons/SkeletonCard";

const NewBlogs = () => {
    const { props: sharedProps } = usePage();
    const { buckets, tenantSlug } = sharedProps;

    const profileImagesBucket = buckets["profile_images"].name;
    const profileImagesRegion = buckets["profile_images"].region;
    const profileImageBaseUrl = `https://${profileImagesBucket}.s3.${profileImagesRegion}.amazonaws.com/${tenantSlug}/`;

    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const retrieveLatestBlogs = async () => {
            try {
                const url = route("internal.blogs.recent");
                const response = await ApiService.get(url);

                if (response.success) {
                    setBlogs(response.latestBlogs || []);
                }
            } catch (error) {
                console.error("Error retrieving blogs:", error);
            } finally {
                setLoading(false);
            }
        };

        retrieveLatestBlogs();
    }, []);

    if (loading) {
        return (
            <div className="pb-5">
                <SkeletonCard />
                <SkeletonCard />
            </div>
        );
    }

    if (!blogs || blogs.length === 0) {
        return (
            <div className="pb-5">
                <div className="bg-white dark:bg-boxdark text-center p-6 rounded shadow font-light text-gray-600 dark:text-gray-300">
                    <h3 className="pb-2 text-xl font-semibold text-center text-black dark:text-white">
                        New Blogs
                    </h3>
                    <hr className="mb-4 border-t border-gray-300 dark:border-strokedark opacity-20 w-3/4 mx-auto" />
                    <div
                        style={{ height: "465px" }}
                        className="flex items-center justify-center h-full"
                    >
                        No Blogs Yet!
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="bg-white dark:bg-boxdark px-5 pt-4 pb-6 shadow rounded-md">
                <h3 className="pb-2 text-xl font-semibold text-center text-black dark:text-white">
                    New Blogs
                </h3>
                <hr className="mb-4 border-t border-gray-300 dark:border-strokedark opacity-20 w-3/4 mx-auto" />
                <div className="space-y-6">
                    {blogs.map((blog, index) => (
                        <div key={index} className="flex gap-4">
                            <div className="shrink-0">
                                <Link
                                    href={route("user.users.show", {
                                        username: blog.owner.user_name,
                                    })}
                                >
                                    <span className="h-12 w-12 rounded-full">
                                        <img
                                            className="ml-1 mt-1 w-8 rounded-full"
                                            src={
                                                blog?.owner
                                                    ?.avatar_image_file_name
                                                    ? profileImageBaseUrl +
                                                      blog.owner
                                                          .avatar_image_file_name
                                                    : UserOne
                                            }
                                            alt="User"
                                        />
                                    </span>
                                </Link>
                            </div>
                            <div className="flex-1">
                                <div className="mb-1 text-sm text-gray-600 dark:text-gray-300">
                                    <Link
                                        href={
                                            blog.owner
                                                ? `/users/${blog.owner.user_name}`
                                                : `/organization/${blog.name}`
                                        }
                                        className="font-semibold text-gray-800 dark:text-white hover:underline"
                                    >
                                        {blog.owner
                                            ? blog.owner.first_name
                                            : blog.name}
                                    </Link>{" "}
                                    added a new blog
                                    <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                        {blog.created_at && (
                                            <span>
                                                {formatDistanceToNow(
                                                    new Date(blog.created_at),
                                                    {
                                                        addSuffix: true,
                                                    },
                                                )}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="text-gray-700 dark:text-gray-300 text-sm">
                                    <Link
                                        href={route("users.blogs.show", {
                                            blog_url_name: blog.url_name,
                                        })}
                                        className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
                                    >
                                        {blog.title}
                                    </Link>
                                    <ReadMoreText
                                        maxCharCount={80}
                                        text={
                                            blog.preview_text
                                                ? blog.preview_text
                                                : ""
                                        }
                                    />
                                    {blog.imageUrls &&
                                        blog.imageUrls.length > 0 && (
                                            <img
                                                src={blog.imageUrls[0]}
                                                alt="Blog"
                                                className="mt-2 rounded-md max-h-48 object-cover w-full"
                                                style={{ maxWidth: "50%" }}
                                            />
                                        )}
                                </div>
                                <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                    <Link
                                        href={route("users.blogs.show", {
                                            blog_url_name: blog.url_name,
                                        })}
                                        className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:underline font-bold"
                                    >
                                        <svg
                                            className="mr-1"
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <rect
                                                width="24"
                                                height="24"
                                                rx="4"
                                                fill="#205AA7"
                                            />
                                            <path
                                                d="M7 17L17 7M17 7H9M17 7V15"
                                                stroke="white"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                        View Blog
                                    </Link>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default NewBlogs;
